import { AlertTriangle, Clock, Inbox, Timer } from 'lucide-react'
import { formatHours } from '@/lib/myTasks'
import type { QuickFilter } from '@/lib/myTasksViews'
import { cn } from '@/lib/utils'

/**
 * The window in four numbers. Three of them are also one-click filters: show
 * only nobody's work, only late work, or only the people over capacity.
 */
export function SummaryChips({
  stats,
  showTeamStats,
  quick,
  onlyOverloaded,
  onQuick,
  onOverloaded,
}: {
  stats: { plannedHrs: number; unassigned: number; overloadedPeople: number; overdue: number }
  /** Unassigned and overloaded people are a supervisor's numbers. */
  showTeamStats: boolean
  quick: QuickFilter
  onlyOverloaded: boolean
  onQuick: (q: QuickFilter) => void
  onOverloaded: (on: boolean) => void
}) {
  const chip = (active: boolean, tone: string) =>
    cn(
      'flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-left shadow-sm ring-1 ring-border transition-colors hover:bg-muted/60',
      active && cn('ring-2', tone),
    )
  return (
    <div className='flex flex-wrap items-center gap-1.5' aria-label='Summary'>
      <div className='flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 shadow-sm ring-1 ring-border'>
        <Timer className='h-3.5 w-3.5 text-primary' />
        <span className='flex items-baseline gap-1 whitespace-nowrap'>
          <span className='text-sm font-semibold tabular-nums'>{formatHours(stats.plannedHrs)} h</span>
          <span className='text-xs text-muted-foreground'>planned</span>
        </span>
      </div>
      {showTeamStats && (
        <button
          type='button'
          aria-pressed={quick === 'unassigned'}
          onClick={() => onQuick(quick === 'unassigned' ? null : 'unassigned')}
          className={chip(quick === 'unassigned', 'ring-amber-400')}
        >
          <Inbox className='h-3.5 w-3.5 text-amber-600' />
          <span className='flex items-baseline gap-1 whitespace-nowrap'>
            <span className='text-sm font-semibold tabular-nums'>{stats.unassigned}</span>
            <span className='text-xs text-muted-foreground'>unassigned</span>
          </span>
        </button>
      )}
      <button
        type='button'
        aria-pressed={quick === 'overdue'}
        onClick={() => onQuick(quick === 'overdue' ? null : 'overdue')}
        className={chip(quick === 'overdue', 'ring-destructive/60')}
      >
        <Clock className='h-3.5 w-3.5 text-destructive' />
        <span className='flex items-baseline gap-1 whitespace-nowrap'>
          <span className='text-sm font-semibold tabular-nums'>{stats.overdue}</span>
          <span className='text-xs text-muted-foreground'>overdue</span>
        </span>
      </button>
      {showTeamStats && (
        <button
          type='button'
          aria-pressed={onlyOverloaded}
          onClick={() => onOverloaded(!onlyOverloaded)}
          className={chip(onlyOverloaded, 'ring-destructive/60')}
        >
          <AlertTriangle className='h-3.5 w-3.5 text-destructive' />
          <span className='flex items-baseline gap-1 whitespace-nowrap'>
            <span className='text-sm font-semibold tabular-nums'>{stats.overloadedPeople}</span>
            <span className='text-xs text-muted-foreground'>over capacity</span>
          </span>
        </button>
      )}
    </div>
  )
}
